import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowUp, Brain, CheckCircle2, CircleDashed, Flag, Gavel, Globe, HelpCircle, Loader2, Maximize2, MessagesSquare, Minimize2, Sparkles, Workflow, Wrench } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { DraftRestoredNote, MarkdownBody, useFieldDraft } from "@ncp/ui";
import type { AgentState, LeadItem, Mission, MissionState, PhaseKey, Question } from "../mission/engine";
import type { StepLetter } from "../../case/stage/StepLetters";
import { phaseTitle } from "../mission/lead";
import { toolLabel } from "../mission/toolNames";
import { LIVE } from "./AgentCard";
import { useAsk } from "./ask";
import { GrowingInput, TONE_COLOR, Typewriter, iconOf, useT } from "./kit";
import { MemoryNote, MemoryPanel, SourcesPanel } from "./Memory";

/** The lead's emblem: a flat tile with the orchestrator's mark; a green dot while the team is working. */
export function LeadOrb({ speaking, orbRef }: { agents?: AgentState[]; speaking: boolean; orbRef?: React.Ref<HTMLDivElement> }) {
  return (
    <div ref={orbRef} className="agx-lead-orb shrink-0" data-speaking={speaking}>
      <Workflow className="size-5" />
    </div>
  );
}

const STATUS_KEYS: Record<MissionState["lead"]["status"], string> = { idle: "idle", reading: "reading", planning: "planning", dispatching: "dispatching", listening: "listening", asking: "asking", concluding: "concluding", done: "done" };

/**
 * The lead agent's column: its orb and what it is doing, the wave's plan, and its conversation with the officer – what it says, the
 * findings it relays from the agents, the questions only the officer can answer and the gate at the end of each wave. The officer can
 * also ask it anything about the file: that goes to the platform's case assistant, with its tools.
 */
export type LeadTab = "conversation" | "memory" | "sources";

export function LeadPanel({ mission, state, orbRef, wide, onToggleWide, tab, onTab }: { mission: Mission; state: MissionState; orbRef: React.Ref<HTMLDivElement>; wide: boolean; onToggleWide: () => void; tab: LeadTab; onTab: (tab: LeadTab) => void }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const phaseAgents = state.order.map((id) => state.agents[id]!).filter((a) => a.phase === state.phase);
  const live = phaseAgents.filter((a) => LIVE.includes(a.status)).length;
  const waiting = Object.values(state.questions).filter((q) => !q.answer).length;
  const speaking = ["reading", "planning", "dispatching", "concluding"].includes(state.lead.status) || live > 0;
  const scroller = useRef<HTMLDivElement>(null);
  const items = state.lead.items;
  // Questions waiting for the officer stay pinned above the composer; once answered they take their place in the conversation.
  const pending = items.flatMap((i) => (i.kind === "question" && state.questions[i.questionId] && !state.questions[i.questionId]!.answer ? [state.questions[i.questionId]!] : []));
  const shown = items.filter((i) => i.kind !== "question" || !!state.questions[i.questionId]?.answer);

  useEffect(() => {
    const el = scroller.current;
    if (!el || tab !== "conversation") return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [shown.length, waiting, tab]);

  // A memory written or recalled since the officer last looked at the Memory tab marks the tab.
  const [seenMemory, setSeenMemory] = useState(0);
  useEffect(() => { if (tab === "memory") setSeenMemory(state.memory.length); }, [tab, state.memory.length]);
  const reached = Object.keys(state.sources).length;

  return (
    <aside className="agx-glass relative flex h-full min-h-0 flex-col overflow-hidden rounded-[6px]">
      <div className="relative flex items-center gap-3 border-b border-[color:var(--agx-line)] px-3 py-2.5">
        <LeadOrb speaking={speaking} orbRef={orbRef} />
        <button type="button" onClick={onToggleWide} className="agx-btn absolute end-2 top-2 size-7 p-0" data-tone="ghost" aria-label={tr(wide ? "backoffice:agentic.restore" : "backoffice:agentic.maximize")} title={tr(wide ? "backoffice:agentic.restore" : "backoffice:agentic.maximize")}>
          {wide ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="flex items-baseline gap-2"><span className="font-heading text-[15px] font-semibold leading-tight">{tr("backoffice:agentic.lead.name")}</span><span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--agx-gold)]">{tr("backoffice:agentic.lead.kicker")}</span></p>
          <p className={`truncate text-[12px] ${state.lead.status === "done" ? "agx-dim" : "agx-shimmer"}`}>{t(state.lead.line)}</p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-cyan)" }}>{tr("backoffice:agentic.lead.active", { count: live })}</span>
            {waiting ? <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-amber)" }}><HelpCircle className="size-2.5" />{tr("backoffice:agentic.lead.waiting", { count: waiting })}</span> : null}
            <span className="agx-chip" style={{ ["--c" as string]: "var(--agx-violet)" }}>{tr(`backoffice:agentic.lead.status.${STATUS_KEYS[state.lead.status]}`)}</span>
          </div>
        </div>
      </div>

      <Plan state={state} folded={pending.length > 0} />

      <div className="flex items-center gap-1 border-b border-[color:var(--agx-line)] px-2" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "conversation"} className="agx-tab" data-active={tab === "conversation"} onClick={() => onTab("conversation")}>
          <MessagesSquare className="size-3.5" />{tr("backoffice:agentic.tabs.conversation")}
        </button>
        <button type="button" role="tab" aria-selected={tab === "memory"} className="agx-tab" data-active={tab === "memory"} onClick={() => onTab("memory")}>
          <Brain className="size-3.5" style={{ color: "var(--agx-memory)" }} />{tr("backoffice:agentic.tabs.memory")}<span className="count">{state.memory.length}</span>
          {tab !== "memory" && state.memory.length > seenMemory ? <span className="ping" /> : null}
        </button>
        <button type="button" role="tab" aria-selected={tab === "sources"} className="agx-tab" data-active={tab === "sources"} onClick={() => onTab("sources")}>
          <Globe className="size-3.5" style={{ color: "var(--agx-green)" }} />{tr("backoffice:agentic.tabs.sources")}<span className="count">{reached}</span>
        </button>
      </div>

      {tab === "conversation" ? (
        <div ref={scroller} className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
          <AnimatePresence initial={false}>
            {shown.map((item, i) => (
              <motion.div key={item.id} layout="position" initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.28 }}>
                <StreamItem item={item} state={state} mission={mission} newest={i === shown.length - 1} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {tab === "memory" ? <MemoryPanel state={state} mission={mission} /> : <SourcesPanel state={state} mission={mission} />}
        </div>
      )}

      {pending.length ? (
        <div className="max-h-[46%] shrink-0 space-y-3 overflow-y-auto border-t border-[color:var(--agx-amber)]/30 bg-[color:var(--agx-amber)]/[0.03] p-3">
          <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--agx-amber)]"><HelpCircle className="size-3" />{tr("backoffice:agentic.lead.pending")} · {pending.length}</p>
          <AnimatePresence initial={false}>
            {pending.map((q) => (
              <motion.div key={q.id} layout initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }}>
                <QuestionCard question={q} state={state} mission={mission} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : null}

      <Composer />
    </aside>
  );
}

/** The wave's plan; folded to its heading while questions wait for the officer, so they and the conversation keep the room. */
function Plan({ state, folded }: { state: MissionState; folded: boolean }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const [toggled, setOpen] = useState(true);
  const open = toggled && !folded;
  const steps = state.lead.plan.filter((p) => p.phase === state.phase);
  if (!steps.length) return null;
  return (
    <div className="border-b border-[color:var(--agx-line)] px-3 py-2">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] agx-faint">
        <Flag className="size-3" /> {tr("backoffice:agentic.lead.plan", { phase: t(phaseTitle(state.phase)) })}
        <span className="ms-auto normal-case tracking-normal">{open ? "−" : "+"}</span>
      </button>
      {open ? (
        <ul className="mt-2 space-y-1.5">
          {steps.map((s) => {
            const agents = s.agentIds.map((id) => state.agents[id]).filter(Boolean) as AgentState[];
            const done = agents.filter((a) => a.status === "done" || a.status === "flagged" || a.status === "failed").length;
            const waiting = agents.some((a) => a.status === "waiting");
            const all = agents.length > 0 && done === agents.length;
            return (
              <li key={s.id} className="flex items-center gap-2 text-[12px]">
                {all ? <CheckCircle2 className="size-3.5 text-[color:var(--agx-green)]" /> : waiting ? <HelpCircle className="size-3.5 text-[color:var(--agx-amber)]" /> : agents.length ? <Loader2 className="size-3.5 agx-spin text-[color:var(--agx-cyan)]" /> : <CircleDashed className="size-3.5 agx-faint" />}
                <span className={all ? "agx-dim" : ""}>{t(s.label)}</span>
                <span className="agx-mono ms-auto text-[11px] agx-faint">{done}/{s.agentIds.length}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function StreamItem({ item, state, mission, newest }: { item: LeadItem; state: MissionState; mission: Mission; newest: boolean }) {
  const t = useT();
  const { t: tr } = useTranslation();
  switch (item.kind) {
    case "phase":
      return (
        <div className="flex items-center gap-3 pt-1">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[color:var(--agx-gold)]/50" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--agx-gold)]">{tr("backoffice:agentic.lead.wave", { n: phaseIndex(item.phase) })} · {t(item.text)}</span>
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[color:var(--agx-gold)]/50" />
        </div>
      );
    case "say":
      return (
        <div className="agx-bubble">
          <div className="flex gap-2.5 p-3 text-[13px] leading-[1.55]">
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-[color:var(--agx-gold)]" />
            <p>{newest ? <Typewriter key={item.id} text={t(item.text)} ms={Math.min(5000, t(item.text).length * 16)} active={Date.now() - item.at < 6000} /> : t(item.text)}</p>
          </div>
        </div>
      );
    case "relay": {
      const agent = state.agents[item.agentId];
      const finding = agent?.findings.find((f) => f.id === item.findingId);
      if (!agent || !finding) return null;
      const Icon = iconOf(agent.icon);
      return (
        <button type="button" onClick={() => mission.setFocus(agent.id)} data-squad={agent.squad} className="flex w-full items-start gap-2.5 rounded-[5px] border border-[color:var(--agx-line)] agx-wash agx-hover p-2.5 text-start" style={{ borderInlineStartColor: TONE_COLOR[finding.tone], borderInlineStartWidth: 2 }}>
          <span className="agx-orb mt-0.5 size-6 shrink-0"><Icon className="size-3" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] font-semibold" style={{ color: "var(--sq)" }}>{t(agent.name)}</span>
            <span className="block text-[12.5px] leading-[1.5] text-[color:var(--agx-text)]/90">{t(finding.text)}</span>
            {finding.source ? <span className="mt-0.5 block text-[11px] agx-faint">↳ {t(finding.source)}</span> : null}
          </span>
        </button>
      );
    }
    case "question": {
      const q = state.questions[item.questionId];
      return q ? <QuestionCard question={q} state={state} mission={mission} /> : null;
    }
    case "user":
      return <div className="ms-10 rounded-md rounded-ee-sm bg-[color:var(--agx-gold)]/15 px-3 py-2 text-[13px] text-[color:var(--agx-gold-2)]">{item.text}</div>;
    case "memory": {
      const memory = state.memory.find((m) => m.id === item.memoryId);
      return memory ? <MemoryNote item={memory} mission={mission} state={state} size="lead" onAgent={(id) => { if (id !== "lead") mission.setFocus(id); }} /> : null;
    }
    case "chat":
      return (
        <div className="agx-bubble">
          <div className="p-3 text-[13px] leading-[1.6]">
            {item.tools.length ? <div className="mb-2 flex flex-wrap gap-1">{[...new Set(item.tools.map((tool) => t(toolLabel(tool))))].map((label) => <span key={label} className="agx-chip" style={{ ["--c" as string]: "var(--agx-violet)" }}><Wrench className="size-2.5" />{label}</span>)}</div> : null}
            {item.text ? <div className="agx-md text-[13px] [&_a]:text-[color:var(--agx-cyan)] [&_p]:my-1 [&_ul]:my-1"><MarkdownBody streaming={item.streaming}>{item.text}</MarkdownBody></div> : <p className="agx-shimmer">{tr("backoffice:agentic.lead.thinking")}</p>}
            {item.error ? <p className="mt-1 text-[12px] text-[color:var(--agx-rose)]">{item.error}</p> : null}
          </div>
        </div>
      );
  }
}

const phaseIndex = (p: PhaseKey) => ["screening", "publication", "review", "assessment", "decision"].indexOf(p) + 1;

/** A short, stable key for a question's text. */
const textKey = (text: string) => [...text].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0).toString(36);

function QuestionCard({ question, state, mission }: { question: Question; state: MissionState; mission: Mission }) {
  const t = useT();
  const { t: tr } = useTranslation();
  // The officer's own words, when the question asks for them (an override's reasons): the field starts empty and nothing is written in it.
  const [typed, setTyped] = useState("");
  // Kept on this device until answered (S 2.7). A question's id is new on every run, so the reasons are filed under the agent, the phase
  // and the question's own words: the same question asked again after a reload gets them back.
  const kept = useFieldDraft(question.input ? `agentic:${state.caseId}:${question.agentId}:${question.phase}:${textKey(question.text.en)}:reasons` : null, typed, setTyped, { enabled: !question.answer });
  const short = typed.trim().length < (question.input?.minLength ?? 1);
  const agent = state.agents[question.agentId];
  const gate = question.kind === "gate";
  const answered = !!question.answer;
  const Icon = gate ? Gavel : agent ? iconOf(agent.icon) : Sparkles;
  return (
    <div className={`agx-question p-3.5 ${gate ? "agx-gate" : ""}`} data-answered={answered} data-squad={agent?.squad}>
      <div className="mb-2 flex items-center gap-2">
        <span className={`grid size-6 place-items-center rounded-full ${gate ? "bg-[color:var(--agx-gold)] text-[color:var(--agx-on-accent)]" : "agx-orb"}`}><Icon className="size-3" /></span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: gate ? "var(--agx-gold)" : "var(--agx-amber)" }}>
          {gate ? tr("backoffice:agentic.lead.gate", { phase: t(phaseTitle(question.phase)) }) : tr("backoffice:agentic.lead.question", { agent: agent ? t(agent.name) : tr("backoffice:agentic.lead.name") })}
        </span>
      </div>
      <p className="text-[13.5px] font-semibold leading-[1.45]">{t(question.text)}</p>
      {question.detail ? <p className="mt-1 text-[12px] leading-[1.5] agx-dim">{t(question.detail)}</p> : null}
      {question.evidence?.length ? (
        <dl className="mt-2.5 space-y-1 rounded-[5px] border border-[color:var(--agx-line)] agx-sunk p-2">
          {question.evidence.map((e, i) => (
            <div key={i} className="flex items-start gap-2 text-[11.5px]">
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full" style={{ background: TONE_COLOR[e.tone ?? "info"] }} />
              <dt className="agx-dim">{t(e.label)}</dt>
              <dd className="ms-auto text-end font-medium">{t(e.value)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {question.letters?.length ? <StepLetterSheets letters={question.letters} /> : null}
      {question.input ? (
        answered ? (
          question.answerText ? <p className="mt-2.5 whitespace-pre-wrap rounded-[5px] border border-[color:var(--agx-line)] agx-sunk p-2 text-[12.5px] leading-[1.5]" dir="auto" data-no-translate="">{question.answerText}</p> : null
        ) : (
          <label className="mt-2.5 block">
            <span className="block text-[11.5px] font-medium">{t(question.input.label)}</span>
            {question.input.hint ? <span className="block text-[11px] agx-faint">{t(question.input.hint)}</span> : null}
            {kept.restored ? <DraftRestoredNote onDiscard={kept.discard} /> : null}
            <textarea value={typed} onChange={(e) => setTyped(e.target.value)} rows={3} dir="auto"
              className="mt-1.5 block w-full resize-y rounded-[5px] border border-[color:var(--agx-line-2)] agx-sunk px-2 py-1.5 text-[12.5px] leading-[1.5] text-[color:var(--agx-text)] outline-none focus:border-[color:var(--agx-gold)]/60" />
          </label>
        )
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {question.options.map((o) => {
          const chosen = question.answer === o.id;
          return (
            <button key={o.id} type="button" disabled={answered || (o.needsText && short)} onClick={() => mission.answer(question.id, o.id, o.needsText ? typed : undefined)} className="agx-btn flex-col items-start gap-0 text-start disabled:opacity-50" data-tone={answered ? (chosen ? "primary" : "ghost") : o.tone === "primary" ? "primary" : o.tone === "neutral" ? "ghost" : undefined} style={answered && !chosen ? { opacity: 0.4 } : undefined}>
              <span>{chosen ? "✓ " : ""}{t(o.label)}</span>
              {o.hint && !answered ? <span className="text-[11px] font-normal opacity-75">{t(o.hint)}</span> : null}
            </button>
          );
        })}
      </div>
      {question.outcome ? <p className="mt-2.5 text-[12px] leading-[1.5]" style={{ color: TONE_COLOR[question.outcomeTone ?? "ok"] }}>{t(question.outcome)}</p> : answered && gate ? <p className="mt-2.5 flex items-center gap-1.5 text-[12px] agx-dim"><Loader2 className="size-3 agx-spin" />{tr("backoffice:agentic.lead.carrying")}</p> : null}
    </div>
  );
}

/**
 * The letters a step sends, in full, on the officer's card before the officer confirms it (release 1.6): what each is, to whom, whether it has
 * legal effect, and its English and Arabic text as the step will send them.
 */
function StepLetterSheets({ letters }: { letters: StepLetter[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  return (
    <div className="mt-2.5 space-y-2" data-step-letters={letters.length}>
      {letters.map((l, i) => (
        <div key={`${l.source}:${i}`} className="rounded-[5px] border border-[color:var(--agx-line)] agx-sunk p-2" data-step-letter={l.source}>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] agx-faint">{t(`backoffice:stepLetters.sources.${l.source}`, { defaultValue: l.templateKey })}</p>
          <p className="mt-0.5 text-[12.5px] font-semibold leading-[1.4]" dir="auto">{ar ? l.subjectAr : l.subject}</p>
          <p className="text-[11.5px] agx-dim" dir="auto">{t("backoffice:stepLetters.to", { name: ar && l.recipientNameAr ? l.recipientNameAr : l.recipientName })} · {l.legalEffect ? t("backoffice:stepLetters.legalEffect") : t("backoffice:stepLetters.courtesy")}</p>
          {([["en", "ltr", t("backoffice:stepLetters.english"), l.subject, l.body], ["ar", "rtl", t("backoffice:stepLetters.arabic"), l.subjectAr, l.bodyAr]] as const).map(([lang, dir, label, subject, body]) => (
            <div key={lang} className="mt-2 border-t border-[color:var(--agx-line)] pt-1.5">
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] agx-faint">{label}</p>
              <div lang={lang} dir={dir} className="max-h-56 overflow-y-auto text-[12px] leading-[1.5]" tabIndex={0} role="region" aria-label={`${label} · ${subject}`}>
                <p className="font-medium">{subject}</p>
                <MarkdownBody>{body}</MarkdownBody>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Free text to the lead: answered by the platform's case assistant (the same conversation the case's Assistant button opens). */
function Composer() {
  const { t } = useTranslation();
  const { ask, answering } = useAsk();
  const [text, setText] = useState("");
  const busy = answering === "lead";
  const send = () => {
    const value = text.trim();
    if (!value || busy) return;
    setText("");
    ask("lead", value);
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[color:var(--agx-line)] p-3">
      <div className="flex items-end gap-2 rounded-md border border-[color:var(--agx-line-2)] agx-sunk p-1.5 focus-within:border-[color:var(--agx-gold)]/60">
        <GrowingInput value={text} onChange={setText} onSend={send} max={160} placeholder={t("backoffice:agentic.lead.ask")} aria-label={t("backoffice:agentic.lead.ask")}
          className="min-h-9 min-w-0 flex-1 px-2 py-2 text-[13px] leading-5 text-[color:var(--agx-text)] placeholder:text-[color:var(--agx-faint)]" />
        <button type="submit" disabled={!text.trim() || busy} className="agx-btn size-9 shrink-0 p-0" data-tone="primary" aria-label={t("backoffice:agentic.lead.send")}>
          {answering === "lead" ? <Loader2 className="size-4 agx-spin" /> : <ArrowUp className="size-4" />}
        </button>
      </div>
      <p className="mt-1.5 px-1 text-[11px] agx-faint">{t("backoffice:agentic.lead.askHint")}</p>
    </form>
  );
}
